import {
  createApiHeaders,
  tenantAwareApiWrapper,
  withServiceClient,
  getBaseUrlOverride,
} from './portal-http';

import axios from 'axios';
import { conversationAttachmentsClient } from '@terra/shared-rest-clients-conversation-attachments';
import type { ConversationAttachmentsContract } from '@terra/shared-contracts-conversation-attachments';
import { type ConversationAttachment } from '@terra/shared-contracts-conversation-attachments';
import type { ConversationCreatedConversation } from '@terra/shared-dto-chat-types';

export const portalChatApi = {
  getConversations: async ({
    targetId,
    search,
  }: {
    targetId?: string;
    search?: string;
  } = {}) => {
    const headers = await createApiHeaders();
    const searchParams = new URLSearchParams();

    if (targetId?.trim()) {
      searchParams.set('targetId', targetId.trim());
    }

    if (search?.trim()) {
      searchParams.set('search', search.trim());
    }

    const query = searchParams.toString();
    const response = await fetch(
      `${getBaseUrlOverride('chat')}/conversation${query ? `?${query}` : ''}`,
      {
        credentials: 'include',
        headers,
      },
    );

    if (!response.ok) {
      throw new Error('Failed to fetch conversations');
    }

    return response.json() as Promise<{
      conversations: ConversationCreatedConversation[];
    }>;
  },

  deleteConversation: async (conversationId: string) => {
    const headers = await createApiHeaders();
    const response = await fetch(
      `${getBaseUrlOverride('chat')}/conversation/${conversationId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers,
      },
    );

    if (!response.ok) {
      throw new Error('Failed to delete conversation');
    }

    return response.json() as Promise<{ success: boolean }>;
  },

  updateConversation: async ({
    conversationId,
    title,
    description,
  }: {
    conversationId: string;
    title?: string;
    description?: string;
  }) => {
    const headers = await createApiHeaders();
    const response = await fetch(
      `${getBaseUrlOverride('chat')}/conversation/${conversationId}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(title !== undefined ? { title } : {}),
          ...(description !== undefined ? { description } : {}),
        }),
      },
    );

    if (!response.ok) {
      throw new Error('Failed to update conversation');
    }

    return response.json() as Promise<ConversationCreatedConversation>;
  },

  listConversationAttachments: (threadId: string) =>
    tenantAwareApiWrapper<
      typeof ConversationAttachmentsContract.listConversationAttachments
    >({
      endpointFn: (headers) =>
        conversationAttachmentsClient.listConversationAttachments({
          params: { threadId },
          ...withServiceClient(headers, 'chat'),
        }),
    }),

  deleteConversationAttachment: ({
    threadId,
    attachmentId,
  }: {
    threadId: string;
    attachmentId: string;
  }) =>
    tenantAwareApiWrapper<
      typeof ConversationAttachmentsContract.deleteConversationAttachment
    >({
      endpointFn: (headers) =>
        conversationAttachmentsClient.deleteConversationAttachment({
          params: { threadId, attachmentId },
          ...withServiceClient(headers, 'chat'),
        }),
    }),

  uploadConversationAttachments: async ({
    threadId,
    applicationId,
    files,
    onUploadProgress,
  }: {
    threadId: string;
    applicationId?: string;
    files: File[];
    onUploadProgress?: (progressEvent: {
      loaded: number;
      total?: number;
    }) => void;
  }): Promise<{ attachments: ConversationAttachment[] }> => {
    try {
      const headers = await createApiHeaders();
      const formData = new FormData();

      files.forEach((file) => formData.append('files', file));
      if (applicationId) {
        formData.append('applicationId', applicationId);
      }

      const baseUrl = getBaseUrlOverride('chat');
      const response = await axios.post<{
        attachments: ConversationAttachment[];
      }>(
        `${baseUrl}/conversation-attachments/conversations/${threadId}/attachments`,
        formData,
        {
          headers: { ...headers },
          onUploadProgress: onUploadProgress
            ? (progressEvent) => {
                onUploadProgress({
                  loaded: progressEvent.loaded,
                  total: progressEvent.total,
                });
              }
            : undefined,
        },
      );

      return response.data;
    } catch (error) {
      console.error('Failed to upload conversation attachments:', error);

      throw error;
    }
  },
};
